// Horizontal alignment mathematics — tessellation and station projection.
// All coordinates are in PROJECT space (large XY values from .bim).
//
// Accuracy per primitive type:
//   lineSegment       — closed-form (exact)
//   arc               — closed-form circle projection (exact)
//   transitionSpiral  — Newton-Raphson on perpendicularity condition (P(s)−Q)·T(s)=0,
//                       P(s) evaluated by 8-point Gauss-Legendre quadrature.
//                       Converges to < 1e-9 m in 4-6 iterations.
//                       No closed form exists; see: doi.org/10.1137/18M1200439

// ── Gauss-Legendre 8-point quadrature constants (on [-1, 1]) ─────────────────
const GL8_X = [
  -0.9602898564975363, -0.7966664774136267,
  -0.5255324099163290, -0.1834346424956498,
   0.1834346424956498,  0.5255324099163290,
   0.7966664774136267,  0.9602898564975363,
];
const GL8_W = [
  0.1012285362903763, 0.2223810344533745,
  0.3137066458778873, 0.3626837833783620,
  0.3626837833783620, 0.3137066458778873,
  0.2223810344533745, 0.1012285362903763,
];

// ── Format station ─────────────────────────────────────────────────────────────
// Civil engineering notation: km+m.mmm  e.g. 20001 → "20+001.000"
export function formatStation(stationMeters) {
  if (stationMeters == null || isNaN(stationMeters)) return "—";
  const km = Math.floor(stationMeters / 1000);
  const m  = stationMeters - km * 1000;
  return `${km}+${m.toFixed(3).padStart(7, "0")}`;
}

// ── Tessellate alignment ───────────────────────────────────────────────────────
// Converts raw horizontalGeometry.path into:
//   points[]       — dense [x,y] sample array for drawing the path line
//   cumulLengths[] — station at each point
//   primitives[]   — one entry per path segment with type + exact parameters
//                    used by projectToAlignment for rigorous math per type
export function tessellateAlignment(path, startStation = 0) {
  const points       = [];
  const cumulLengths = [];
  const primitives   = [];
  let   cumLen       = startStation;

  for (const entry of path) {
    const type = Object.keys(entry)[0];
    const data = entry[type];

    // ── Line segment ───────────────────────────────────────────────────────────
    if (type === "lineSegment") {
      const [[x1, y1], [x2, y2]] = data;
      const len = Math.hypot(x2 - x1, y2 - y1);

      if (points.length === 0) { points.push([x1, y1]); cumulLengths.push(cumLen); }
      const segStart = cumLen;
      cumLen += len;
      points.push([x2, y2]);
      cumulLengths.push(cumLen);

      primitives.push({ type: "line", startStation: segStart, endStation: cumLen,
        x1, y1, x2, y2 });

    // ── Circular arc ──────────────────────────────────────────────────────────
    } else if (type === "arc") {
      const { center, vectorX, vectorY, sweepStartEnd } = data;
      const [cx, cy] = center;
      const [sx, ex] = sweepStartEnd;
      const totalSweepDeg = ex - sx;
      const r   = Math.hypot(vectorX[0], vectorX[1]);
      const len = r * Math.abs(totalSweepDeg) * Math.PI / 180;

      // Tessellate at ~1°/step for drawing
      const N      = Math.max(8, Math.ceil(Math.abs(totalSweepDeg)));
      const segLen = len / N;
      const segStart = cumLen;

      for (let i = 0; i <= N; i++) {
        const deg = sx + (i / N) * totalSweepDeg;
        const rad = deg * Math.PI / 180;
        const x = cx + vectorX[0] * Math.cos(rad) + vectorY[0] * Math.sin(rad);
        const y = cy + vectorX[1] * Math.cos(rad) + vectorY[1] * Math.sin(rad);
        if (i === 0) {
          if (points.length === 0) { points.push([x, y]); cumulLengths.push(cumLen); }
        } else {
          cumLen += segLen;
          points.push([x, y]);
          cumulLengths.push(cumLen);
        }
      }

      primitives.push({
        type: "arc", startStation: segStart, endStation: cumLen,
        cx, cy,
        vX: [vectorX[0], vectorX[1]],
        vY: [vectorY[0], vectorY[1]],
        r, sweepStart: sx, sweepEnd: ex,
      });

    // ── Transition spiral (clothoid) ──────────────────────────────────────────
    } else if (type === "transitionSpiral") {
      const { origin, xyVectors, startRadius, endRadius, endBearing } = data;

      const kappa0 = startRadius === 0 ? 0 : 1 / startRadius;
      const kappa1 = endRadius   === 0 ? 0 : 1 / endRadius;
      const kappaAvg = (kappa0 + kappa1) / 2;
      if (kappaAvg < 1e-12) continue;

      const tauRad = endBearing * Math.PI / 180; // endBearing is in degrees
      const L = tauRad / kappaAvg;
      if (L < 0.01) continue;

      // Curvature sign from cross-product of tangent × normal
      const [tx, ty] = xyVectors[0];
      const [nx, ny] = xyVectors[1];
      const sign  = (tx * ny - ty * nx) > 0 ? 1 : -1;
      const theta0 = Math.atan2(ty, tx);

      // θ(s) = theta0 + kappaA·s + kappaB·s²
      // where kappaA = signed initial curvature, kappaB = signed half rate-of-change
      const kappaA = sign * kappa0;
      const kappaB = sign * (kappa1 - kappa0) / (2 * L);

      const segStart = cumLen;

      // Tessellate with 60 steps — only for drawing the path line
      const N  = 60;
      const ds = L / N;
      let x = origin[0], y = origin[1];
      if (points.length === 0) { points.push([x, y]); cumulLengths.push(cumLen); }

      for (let i = 0; i < N; i++) {
        const s       = i * ds;
        const bearing = theta0 + kappaA * s + kappaB * s * s;
        x += Math.cos(bearing) * ds;
        y += Math.sin(bearing) * ds;
        cumLen += ds;   // arc-length step — matches Newton-Raphson's s parameterisation
        points.push([x, y]);
        cumulLengths.push(cumLen);
      }

      // cumLen is now exactly segStart + L
      primitives.push({
        type: "spiral", startStation: segStart, endStation: segStart + L,
        origin: [origin[0], origin[1]], theta0, kappaA, kappaB, L,
      });
    }
  }

  return { points, cumulLengths, primitives };
}

// ── Position and tangent at a given station ────────────────────────────────────
// Returns { x, y, tx, ty } in project coordinates. tx/ty is the unit tangent.
export function getPositionAtStation(tessellated, station) {
  const { points, cumulLengths } = tessellated;
  const n = points.length;
  if (n < 2) return null;

  if (station <= cumulLengths[0]) {
    const [x0, y0] = points[0], [x1, y1] = points[1];
    const len = Math.hypot(x1 - x0, y1 - y0) || 1;
    return { x: x0, y: y0, tx: (x1 - x0) / len, ty: (y1 - y0) / len };
  }
  if (station >= cumulLengths[n - 1]) {
    const [x0, y0] = points[n - 2], [x1, y1] = points[n - 1];
    const len = Math.hypot(x1 - x0, y1 - y0) || 1;
    return { x: x1, y: y1, tx: (x1 - x0) / len, ty: (y1 - y0) / len };
  }

  let i = cumulLengths.findIndex(c => c >= station);
  if (i <= 0) i = 1;
  const t = (station - cumulLengths[i - 1]) / (cumulLengths[i] - cumulLengths[i - 1]);
  const [x0, y0] = points[i - 1], [x1, y1] = points[i];
  const len = Math.hypot(x1 - x0, y1 - y0) || 1;
  return {
    x:  x0 + t * (x1 - x0),
    y:  y0 + t * (y1 - y0),
    tx: (x1 - x0) / len,
    ty: (y1 - y0) / len,
  };
}

// ── Clothoid point evaluation ──────────────────────────────────────────────────
// Evaluates P(s) = origin + ∫₀ˢ [cos θ(t), sin θ(t)] dt
// using 8-point Gauss-Legendre quadrature.
// For railway spirals (slowly varying θ), 8 points gives < 10⁻¹² m error.
function clothoidPointAt(s, prim) {
  const { origin, theta0, kappaA, kappaB } = prim;
  const half = s / 2;
  let sx = 0, sy = 0;
  for (let i = 0; i < 8; i++) {
    const t     = half * (1 + GL8_X[i]);
    const theta = theta0 + kappaA * t + kappaB * t * t;
    sx += GL8_W[i] * Math.cos(theta);
    sy += GL8_W[i] * Math.sin(theta);
  }
  return [origin[0] + half * sx, origin[1] + half * sy];
}

// ── Exact arc projection ───────────────────────────────────────────────────────
// Projects (qx,qy) onto the arc using closed-form circle math.
function projectToArcExact(qx, qy, prim) {
  const { cx, cy, vX, vY, r, sweepStart, sweepEnd, startStation } = prim;

  // Unit vectors of the arc's local frame (vX/vY encode the radius)
  const ux = vX[0] / r, uy = vX[1] / r;
  const wx = vY[0] / r, wy = vY[1] / r;

  // Project Q into arc-local frame and find its angle
  const dx = qx - cx, dy = qy - cy;
  const localX = dx * ux + dy * uy;
  const localY = dx * wx + dy * wy;
  let theta = Math.atan2(localY, localX) * 180 / Math.PI;

  // Normalise to ±180° around sweepStart so clamping is unambiguous
  let alpha = theta - sweepStart;
  while (alpha >  180) alpha -= 360;
  while (alpha < -180) alpha += 360;

  // Clamp to the sweep range [0, 1] in arc parameter
  const totalSweep = sweepEnd - sweepStart; // signed: +CCW, −CW
  const t      = Math.max(0, Math.min(1, alpha / totalSweep));
  const thetaC = sweepStart + t * totalSweep;

  // Foot point
  const tRad = thetaC * Math.PI / 180;
  const fx   = cx + r * (ux * Math.cos(tRad) + wx * Math.sin(tRad));
  const fy   = cy + r * (uy * Math.cos(tRad) + wy * Math.sin(tRad));

  return {
    station:  startStation + r * Math.abs(t * totalSweep) * Math.PI / 180,
    distance: Math.hypot(qx - fx, qy - fy),
    foot:     [fx, fy],
  };
}

// ── Newton-Raphson spiral projection ──────────────────────────────────────────
// Finds s* minimising |P(s)−Q| by solving (P(s)−Q)·T(s) = 0.
//
// Derivation (ref: doi.org/10.1137/18M1200439):
//   f(s)  = (P(s)−Q)·T(s)          [perpendicularity condition]
//   f'(s) = T(s)·T(s) + (P(s)−Q)·κ(s)N(s)
//          = 1 + κ(s)·(P(s)−Q)·N(s)
//   where T(s) = [cos θ, sin θ],  N(s) = [−sin θ, cos θ],  κ(s) = κA + 2κB·s
//
// sInit: initial estimate from the cheap polyline scan (already within the spiral)
function projectToSpiralNewton(qx, qy, prim, sInit) {
  const { L, theta0, kappaA, kappaB, startStation } = prim;
  let s = Math.max(0, Math.min(L, sInit));

  for (let iter = 0; iter < 10; iter++) {
    const [px, py] = clothoidPointAt(s, prim);
    const theta = theta0 + kappaA * s + kappaB * s * s;
    const Tx = Math.cos(theta), Ty = Math.sin(theta); // unit tangent
    const dx = px - qx,        dy = py - qy;          // foot − query

    const f     = dx * Tx + dy * Ty;                         // residual
    const kappa = kappaA + 2 * kappaB * s;                   // curvature at s
    const fp    = 1 + kappa * (-Ty * dx + Tx * dy);          // f'

    if (Math.abs(fp) < 1e-12) break;
    const step = f / fp;
    s = Math.max(0, Math.min(L, s - step));
    if (Math.abs(step) < 1e-9) break; // converged
  }

  const [fx, fy] = clothoidPointAt(s, prim);
  return {
    station:  startStation + s,
    distance: Math.hypot(qx - fx, qy - fy),
    foot:     [fx, fy],
  };
}

// ── Project point onto alignment ───────────────────────────────────────────────
// Two-phase:
//   1. O(N) polyline scan → coarse best match and initial s estimate
//   2. Exact refinement per primitive type
// Returns { station, distance, foot: [x, y] }
export function projectToAlignment(qx, qy, tessellated) {
  const { points, cumulLengths, primitives } = tessellated;

  // ── Phase 1: coarse polyline scan ────────────────────────────────────────────
  let bestDist    = Infinity;
  let bestStation = cumulLengths[0] ?? 0;
  let bestFoot    = points[0] ?? [qx, qy];
  let bestSegIdx  = 0;

  for (let i = 0; i < points.length - 1; i++) {
    const [x1, y1] = points[i], [x2, y2] = points[i + 1];
    const dx = x2 - x1, dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-10) continue;

    const t  = Math.max(0, Math.min(1, ((qx - x1) * dx + (qy - y1) * dy) / len2));
    const fx = x1 + t * dx, fy = y1 + t * dy;
    const d  = Math.hypot(qx - fx, qy - fy);

    if (d < bestDist) {
      bestDist    = d;
      bestFoot    = [fx, fy];
      bestStation = cumulLengths[i] + t * (cumulLengths[i + 1] - cumulLengths[i]);
      bestSegIdx  = i;
    }
  }

  // ── Phase 2: exact projection for the owning primitive ───────────────────────
  // Find which primitive owns the best segment
  const ownerPrim = primitives.find(
    p => bestStation >= p.startStation - 1e-6 && bestStation <= p.endStation + 1e-6
  );

  if (!ownerPrim) {
    return { station: bestStation, distance: bestDist, foot: bestFoot };
  }

  if (ownerPrim.type === "arc") {
    return projectToArcExact(qx, qy, ownerPrim);
  }

  if (ownerPrim.type === "spiral") {
    const sInit = bestStation - ownerPrim.startStation; // local arc-length estimate
    return projectToSpiralNewton(qx, qy, ownerPrim, sInit);
  }

  // Line: polyline scan is already exact for straight segments
  return { station: bestStation, distance: bestDist, foot: bestFoot };
}

// ── Radius at station ───────────────────────────────────────────────────────────
// Returns the curve radius (m) at the given station.
//   Infinity → straight line segment
//   r        → constant arc radius
//   variable → spiral radius 1/κ(s) at that exact point
export function getRadiusAtStation(tessellated, station) {
  const { primitives } = tessellated;
  const prim = primitives.find(
    p => station >= p.startStation - 1e-6 && station <= p.endStation + 1e-6
  );
  if (!prim) return null;
  if (prim.type === 'line') return Infinity;
  if (prim.type === 'arc')  return prim.r;
  if (prim.type === 'spiral') {
    const s     = station - prim.startStation;
    const kappa = Math.abs(prim.kappaA + 2 * prim.kappaB * s);
    return kappa < 1e-10 ? Infinity : 1 / kappa;
  }
  return null;
}

// ── Exact tangent at station ───────────────────────────────────────────────────
// Analytic unit tangent direction { tx, ty } at an exact station, dispatched by primitive
// type — no tessellation/finite-difference approximation. Used for offsetting the QTO
// boundary polygon (buildBoundaryPolygon in BimViewer.js): the boundary previously estimated
// tangent direction from neighboring *sampled* points, which is fine on lines and gentle arcs
// but introduces real error through a spiral, whose whole point is continuously-changing
// curvature — a secant between two nearby samples isn't the true instantaneous tangent there.
// That error gets amplified by the boundary's offset distance (bigger offset = bigger
// positional error from the same angular error), producing a boundary polygon that's
// measurably off through curved/transition sections — verified as the cause of QTO volumes
// coming in consistently lower than ORD's own named-boundary results on a spiral-heavy
// alignment (see docs/milestones/post-M29c-marking-lift-and-qto-boundary-perf.md).
export function getExactTangentAtStation(tessellated, station) {
  const { primitives } = tessellated;
  const prim = primitives.find(
    p => station >= p.startStation - 1e-6 && station <= p.endStation + 1e-6
  );
  if (!prim) return null;

  if (prim.type === 'line') {
    const dx = prim.x2 - prim.x1, dy = prim.y2 - prim.y1;
    const len = Math.hypot(dx, dy) || 1;
    return { tx: dx / len, ty: dy / len };
  }

  if (prim.type === 'arc') {
    const { r, vX, vY, sweepStart, sweepEnd, startStation, endStation } = prim;
    const ux = vX[0] / r, uy = vX[1] / r;
    const wx = vY[0] / r, wy = vY[1] / r;
    const totalSweep = sweepEnd - sweepStart; // signed: +CCW, -CW
    const span = endStation - startStation || 1;
    const t = (station - startStation) / span;
    const thetaC = sweepStart + t * totalSweep;
    const tRad = thetaC * Math.PI / 180;
    const sign = totalSweep >= 0 ? 1 : -1;
    let tx = sign * (-ux * Math.sin(tRad) + wx * Math.cos(tRad));
    let ty = sign * (-uy * Math.sin(tRad) + wy * Math.cos(tRad));
    const len = Math.hypot(tx, ty) || 1;
    return { tx: tx / len, ty: ty / len };
  }

  if (prim.type === 'spiral') {
    const { theta0, kappaA, kappaB, startStation } = prim;
    const s     = station - startStation;
    const theta = theta0 + kappaA * s + kappaB * s * s;
    return { tx: Math.cos(theta), ty: Math.sin(theta) };
  }

  return null;
}
